import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Bot, Loader2, Sparkles, User, X } from "lucide-react";
import { createInitiativeItem, INITIATIVE_LIST_ID } from "../api/graph";
import type { NewInitiativeFields } from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import { useListColumns, choicesFor } from "../hooks/useListColumns";
import { useInitiativeAssist } from "../hooks/useInitiativeAssist";
import { useProfile } from "../hooks/useProfile";
import { logActivity } from "../api/activityLog";
import type { InitiativeItem } from "../types/navigator";
import { ConfirmDialog } from "./ConfirmDialog";

// ============================================================
// Experimental "chat" submission flow — an alternative front end
// to AddInitiativeItemModal, not a replacement. Asks the same
// required questions one at a time, has the AI nudge for more
// substance turn-by-turn, then reuses the exact same bulk review +
// createInitiativeItem save path as the classic form so both flows
// land identical data.
// ============================================================

type ChatFormState = {
  Title: string;
  Yourname: string;
  Team: string;
  Region: string;
  Problem: string;
  Frequency: string;
  ImpactedToday: string;
  IfSolved: string;
  AnticipatedImpact: string;
  ImpactType: string[];
  ProposedSolution: string;
  ExistingExamples: string;
  DataInputs: string;
  ConstraintsRisks: string;
  AnythingElse: string;
};

const EMPTY: ChatFormState = {
  Title: "",
  Yourname: "",
  Team: "",
  Region: "",
  Problem: "",
  Frequency: "",
  ImpactedToday: "",
  IfSolved: "",
  AnticipatedImpact: "",
  ImpactType: [],
  ProposedSolution: "",
  ExistingExamples: "",
  DataInputs: "",
  ConstraintsRisks: "",
  AnythingElse: "",
};

const REGION_FALLBACK = [
  "Atlanta", "Austin", "Corporate", "Calgary", "Edmonton", "NorCal", "Washington",
  "Denver", "Toronto", "Phoenix", "SoCal", "Houston", "Raleigh / Durham", "Charleston",
  "Tampa", "Wilmington", "Dallas", "Portland", "Seattle", "Charlotte", "Spokane",
  "Sacramento", "Columbus", "New Jersey", "Delaware", "Virginia", "Puget Sound", "Weymouth",
];
const FREQUENCY_FALLBACK = ["Daily", "Weekly", "Monthly", "Occasionally", "One-time, but high impact"];
const IMPACT_TYPE_FALLBACK = [
  "Time savings / efficiency",
  "Cost reduction (material, licensing, budget, etc.)",
  "Improved insights and decision quality",
  "Risk reduction",
  "Scalability",
  "Better customer or partner experience",
  "Other",
];

type StepKind = "text" | "select" | "multiselect" | "optional-text";

interface ChatStep {
  key: keyof ChatFormState;
  ask: string;
  label: string; // sent to the AI as the field's identity
  kind: StepKind;
  aiCheck?: boolean;
  /** Passed through to chatCheck — this field can't be waved off with an
   *  honest "don't know" the way internal-knowledge fields can. */
  strict?: boolean;
  /** Included in the final bulk review (the same 8 substantive fields the
   *  classic form reviews) — distinct from aiCheck, since Title/Yourname
   *  now get a per-turn check too but aren't part of that bulk pass. */
  reviewField?: boolean;
}

const STEPS: ChatStep[] = [
  { key: "Title", ask: "Let's start simple — what would you call this initiative?", label: "Initiative title", kind: "text", aiCheck: true, strict: true },
  { key: "Yourname", ask: "And your name, for the record?", label: "Your name", kind: "text", aiCheck: true, strict: true },
  { key: "Team", ask: "What team or function are you with?", label: "Team / function", kind: "text" },
  { key: "Region", ask: "Which region or division is this for?", label: "Region / division", kind: "select" },
  { key: "Problem", ask: "What problem are you trying to solve?", label: "What problem are you trying to solve?", kind: "text", aiCheck: true, reviewField: true, strict: true },
  { key: "Frequency", ask: "How often does this problem come up?", label: "How often does it happen?", kind: "select" },
  { key: "ImpactedToday", ask: "Who's impacted by it, or who'd benefit from a fix?", label: "Who's impacted? Who would benefit?", kind: "text", aiCheck: true, reviewField: true },
  { key: "IfSolved", ask: "If this were solved, what would success actually look like?", label: "If this problem were solved, what would success look like?", kind: "text", aiCheck: true, reviewField: true },
  { key: "AnticipatedImpact", ask: "What impact do you expect — time saved, cost, anything measurable?", label: "Anticipated impact", kind: "text", aiCheck: true, reviewField: true },
  { key: "ImpactType", ask: "What type(s) of impact do you expect? Pick as many as fit, then continue.", label: "Type of impact", kind: "multiselect" },
  { key: "ProposedSolution", ask: "Do you have a proposed AI solution in mind?", label: "Do you have a proposed AI solution in mind?", kind: "text", aiCheck: true, reviewField: true },
  { key: "ExistingExamples", ask: "Any existing examples we could mirror or learn from?", label: "Are there existing examples we could mirror or learn from?", kind: "text", aiCheck: true, reviewField: true },
  { key: "DataInputs", ask: "What data or inputs would be involved?", label: "What data or inputs would be involved?", kind: "text", aiCheck: true, reviewField: true },
  { key: "ConstraintsRisks", ask: "Any constraints, risks, or things to watch out for?", label: "Constraints, risks, or considerations?", kind: "text", aiCheck: true, reviewField: true },
  { key: "AnythingElse", ask: "Last one — a sponsor, or anything else worth mentioning? Totally optional.", label: "Anything else", kind: "optional-text" },
];

const AUTO_CLOSE_MS = 2400;

// `lines` (not a single string) so several back-to-back say() calls render
// as one bubble with multiple lines, instead of a new bubble per call —
// e.g. an acknowledgment + the next question, or a flagged-fields recap +
// its bullet list, all read as a single coherent AI turn.
type ChatMessage = { id: number; from: "ai" | "user"; lines: string[] };

let msgId = 0;
function nextId() {
  msgId += 1;
  return msgId;
}

interface Props {
  existing: InitiativeItem[];
  onClose: () => void;
  onCreated: () => void;
  /** Switches the parent back to the classic form. */
  onBackToForm: () => void;
}

export function AddInitiativeChatModal({ onClose, onCreated, onBackToForm }: Props) {
  const getToken = useGraphToken();
  const { review, chatCheck } = useInitiativeAssist();
  const { profile } = useProfile({ skipPhoto: true });
  const { columns } = useListColumns(INITIATIVE_LIST_ID);

  const regionChoices = useMemo(
    () => choicesFor(columns, "Region_x002f_Division", REGION_FALLBACK),
    [columns]
  );
  const frequencyChoices = useMemo(
    () => choicesFor(columns, "Howoftendoesthisproblemoccur_x00", FREQUENCY_FALLBACK),
    [columns]
  );
  const impactTypeChoices = useMemo(
    () => choicesFor(columns, "Whattypeofimpacttodoyouexpect_x0", IMPACT_TYPE_FALLBACK),
    [columns]
  );

  const [form, setForm] = useState<ChatFormState>(EMPTY);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [attempts, setAttempts] = useState<Partial<Record<keyof ChatFormState, number>>>({});
  const [input, setInput] = useState("");
  const [multiPick, setMultiPick] = useState<string[]>([]);
  const [thinking, setThinking] = useState(false);
  const [phase, setPhase] = useState<"chatting" | "reviewing" | "submitting" | "done">("chatting");
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewRounds, setReviewRounds] = useState(0);
  // When the final review flags fields, we re-ask ONLY those — in order —
  // instead of resuming the normal forward walk through every remaining
  // question (which was the bug: revisiting one field re-triggered the
  // whole rest of the conversation). Empty queue = normal forward flow.
  const [revisitQueue, setRevisitQueue] = useState<(keyof ChatFormState)[]>([]);

  const bodyRef = useRef<HTMLDivElement>(null);
  const greetedRef = useRef(false);
  const prefilledRef = useRef(false);

  const step = revisitQueue.length > 0
    ? STEPS.find((s) => s.key === revisitQueue[0])!
    : STEPS[stepIndex];
  const dirty = messages.length > 0 && phase !== "done";

  // Greet once on mount.
  useEffect(() => {
    if (greetedRef.current) return;
    greetedRef.current = true;
    void logActivity("Feature", "GuidedSubmission", "Opened the AI-guided initiative submission chat");
    setMessages([
      {
        id: nextId(),
        from: "ai",
        lines: [
          "Hi! Let's put your idea together — I'll ask a few questions, one at a time.",
          STEPS[0].ask,
        ],
      },
    ]);
  }, []);

  // Prefill name/team/region from the Graph profile once it loads, and
  // mention it in-chat so it doesn't feel like magic.
  useEffect(() => {
    if (prefilledRef.current || !profile) return;
    prefilledRef.current = true;
    setForm((prev) => {
      const next = { ...prev };
      if (profile.displayName) next.Yourname = profile.displayName;
      if (profile.department) next.Team = profile.department;
      if (profile.officeLocation) {
        const match = regionChoices.find(
          (r) => r.trim().toLowerCase() === profile.officeLocation!.trim().toLowerCase()
        );
        if (match) next.Region = match;
      }
      return next;
    });
  }, [profile, regionChoices]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking]);

  // Appends to the AI's current bubble if it's already mid-turn (the last
  // message is from "ai"), otherwise starts a fresh one — this is what
  // collapses multiple say() calls into a single bubble.
  const say = (text: string) =>
    setMessages((m) => {
      const last = m[m.length - 1];
      if (last?.from === "ai") {
        return [...m.slice(0, -1), { ...last, lines: [...last.lines, text] }];
      }
      return [...m, { id: nextId(), from: "ai", lines: [text] }];
    });
  const hear = (text: string) => setMessages((m) => [...m, { id: nextId(), from: "user", lines: [text] }]);

  const advanceStep = () => {
    if (revisitQueue.length > 0) {
      const remaining = revisitQueue.slice(1);
      setRevisitQueue(remaining);
      if (remaining.length > 0) {
        const nextStep = STEPS.find((s) => s.key === remaining[0])!;
        setInput(String(form[nextStep.key] ?? ""));
        say(nextStep.ask);
      } else {
        void runFinalReview();
      }
      return;
    }
    const next = stepIndex + 1;
    if (next < STEPS.length) {
      const nextStep = STEPS[next];
      const prefilled = form[nextStep.key];
      setStepIndex(next);
      setInput(typeof prefilled === "string" ? prefilled : "");
      say(nextStep.ask);
    } else {
      void runFinalReview();
    }
  };

  const submitTextAnswer = async (raw: string) => {
    const value = raw.trim();
    const isOptional = step.kind === "optional-text";
    if (!value && !isOptional) return;

    hear(value || "(skipped)");
    setForm((f) => ({ ...f, [step.key]: value }));
    setInput("");

    if (!step.aiCheck || !value) {
      advanceStep();
      return;
    }

    setThinking(true);
    try {
      const result = await chatCheck(form.Title || "", step.label, value, step.strict);
      setThinking(false);
      const already = attempts[step.key] ?? 0;
      if (!result.sufficient && already < 1) {
        // Only speak up when the answer needs more — no "Got it!" noise on
        // every pass, just a quiet move to the next question. One gentle
        // follow-up round, then move on regardless (a nudge, not a hard
        // gate like the classic form).
        say(result.message);
        setAttempts((a) => ({ ...a, [step.key]: already + 1 }));
        return;
      }
      advanceStep();
    } catch (err) {
      setThinking(false);
      say("Hmm, I couldn't check that just now — let's keep going.");
      setError(err instanceof Error ? err.message : String(err));
      advanceStep();
    }
  };

  const submitSelectAnswer = (value: string) => {
    hear(value);
    setForm((f) => ({ ...f, [step.key]: value }));
    advanceStep();
  };

  const toggleMultiPick = (opt: string) =>
    setMultiPick((cur) => (cur.includes(opt) ? cur.filter((v) => v !== opt) : [...cur, opt]));

  const confirmMultiPick = () => {
    if (multiPick.length === 0) return;
    hear(multiPick.join(", "));
    setForm((f) => ({ ...f, ImpactType: multiPick }));
    setMultiPick([]);
    advanceStep();
  };

  const FIELD_LABELS_FOR_REVIEW: [keyof ChatFormState, string][] = STEPS.filter(
    (s) => s.reviewField
  ).map((s) => [s.key, s.label]);
  const STRICT_LABELS_FOR_REVIEW = STEPS.filter((s) => s.reviewField && s.strict).map(
    (s) => s.label
  );

  async function runFinalReview() {
    setPhase("reviewing");
    setThinking(true);
    say("Let me take one more look at everything together…");
    try {
      const fields = FIELD_LABELS_FOR_REVIEW.map(([key, label]) => ({
        label,
        value: String(form[key] ?? ""),
      }));
      const issues = await review(form.Title.trim(), fields, STRICT_LABELS_FOR_REVIEW);
      setThinking(false);
      setReviewRounds((r) => r + 1);
      if (issues.length > 0 && reviewRounds < 2) {
        say("A couple of things could use a bit more before this is ready:");
        for (const issue of issues) {
          say(`• ${issue.label} — ${issue.suggestion}`);
        }
        // Queue every flagged field, in question order, so revisiting one
        // doesn't accidentally re-run the rest of the conversation too.
        const flaggedKeys = new Set(
          FIELD_LABELS_FOR_REVIEW.filter(([, label]) =>
            issues.some((i) => i.label === label)
          ).map(([key]) => key)
        );
        const queue = STEPS.filter((s) => flaggedKeys.has(s.key)).map((s) => s.key);
        if (queue.length > 0) {
          setPhase("chatting");
          setRevisitQueue(queue);
          setAttempts({});
          say(`Let's revisit: ${STEPS.find((s) => s.key === queue[0])!.ask}`);
          return;
        }
      }
      say("This looks ready to submit — want me to go ahead?");
      setPhase("reviewing");
    } catch (err) {
      setThinking(false);
      setError(err instanceof Error ? err.message : String(err));
      say("I couldn't run the final check, but you can still submit this now.");
      setPhase("reviewing");
    }
  }

  async function handleSubmit() {
    setPhase("submitting");
    setError(null);
    try {
      const token = await getToken();
      const payload: NewInitiativeFields = {
        Title: form.Title.trim(),
        Yourname: form.Yourname.trim() || undefined,
        Yourteam_x002f_function: form.Team.trim() || undefined,
        Region_x002f_Division: form.Region.trim() || undefined,
        Whatproblemareyoutryingtosolve: form.Problem.trim() || undefined,
        Howoftendoesthisproblemoccur_x00: form.Frequency.trim() || undefined,
        Whoisimpactedbythisproblemtoday_: form.ImpactedToday.trim() || undefined,
        Ifthisproblemweresolved_x002c_wh: form.IfSolved.trim() || undefined,
        Pleaseexplaintheanticipatedimpac: form.AnticipatedImpact.trim() || undefined,
        Whattypeofimpacttodoyouexpect_x0: form.ImpactType.length ? form.ImpactType : undefined,
        ProposedSolution: form.ProposedSolution.trim() || undefined,
        ExistingExamples: form.ExistingExamples.trim() || undefined,
        DataInputs: form.DataInputs.trim() || undefined,
        ConstraintsRisks: form.ConstraintsRisks.trim() || undefined,
        AnythingElse: form.AnythingElse.trim() || undefined,
        Status: "New",
      };
      await createInitiativeItem(token, payload);
      say("Done — your initiative is in the queue. Thanks!");
      setPhase("done");
      window.setTimeout(() => onCreated(), AUTO_CLOSE_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      say("That didn't save — want to try again?");
      setPhase("reviewing");
    }
  }

  const requestClose = () => {
    if (phase === "submitting") return;
    if (dirty) setConfirmOpen(true);
    else onClose();
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submitTextAnswer(input);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && requestClose()}>
        <div className="modal modal-initiative chat-add-modal" role="dialog" aria-modal="true">
          <div className="modal-accent" aria-hidden />
          <div className="modal-head">
            <div className="modal-head-icon icon-initiative" aria-hidden>
              <Sparkles size={22} />
            </div>
            <div className="modal-head-text">
              <span className="modal-kind">AI Initiative · Experimental</span>
              <h2 className="modal-title">Chat with the assistant</h2>
              <button type="button" className="add-item-try-chat" onClick={onBackToForm}>
                ← Back to the regular form
              </button>
            </div>
            <button type="button" className="modal-close" onClick={requestClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="chat-add-body" ref={bodyRef}>
            {messages.map((m) => (
              <div key={m.id} className={`chat-add-msg chat-add-msg--${m.from}`}>
                <span className="chat-add-avatar" aria-hidden>
                  {m.from === "ai" ? <Bot size={14} /> : <User size={14} />}
                </span>
                <span className="chat-add-bubble">
                  {m.lines.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </span>
              </div>
            ))}
            {thinking ? (
              <div className="chat-add-msg chat-add-msg--ai">
                <span className="chat-add-avatar" aria-hidden>
                  <Bot size={14} />
                </span>
                <span className="chat-add-bubble chat-add-bubble--typing" aria-label="Assistant is typing">
                  <i /><i /><i />
                </span>
              </div>
            ) : null}
            {error ? <div className="add-item-error chat-add-error">{error}</div> : null}
          </div>

          <div className="chat-add-foot">
            {phase === "chatting" && step.kind === "select" ? (
              <div className="chat-add-chips">
                {(step.key === "Region" ? regionChoices : frequencyChoices).map((opt) => (
                  <button key={opt} type="button" className="chat-add-chip" onClick={() => submitSelectAnswer(opt)}>
                    {opt}
                  </button>
                ))}
              </div>
            ) : null}

            {phase === "chatting" && step.kind === "multiselect" ? (
              <div className="chat-add-chips">
                {impactTypeChoices.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`chat-add-chip${multiPick.includes(opt) ? " is-picked" : ""}`}
                    onClick={() => toggleMultiPick(opt)}
                  >
                    {opt}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-primary chat-add-continue"
                  disabled={multiPick.length === 0}
                  onClick={confirmMultiPick}
                >
                  Continue
                </button>
              </div>
            ) : null}

            {phase === "chatting" && (step.kind === "text" || step.kind === "optional-text") ? (
              <div className="chat-add-input-row">
                <textarea
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onInputKeyDown}
                  placeholder="Type your answer… (Enter to send)"
                  disabled={thinking}
                  autoFocus
                />
                {step.kind === "optional-text" ? (
                  <button type="button" className="btn btn-ghost" onClick={() => submitTextAnswer("")}>
                    Skip
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={thinking || (!input.trim() && step.kind === "text")}
                  onClick={() => submitTextAnswer(input)}
                >
                  Send
                </button>
              </div>
            ) : null}

            {phase === "reviewing" ? (
              <div className="chat-add-input-row">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={thinking}
                >
                  Submit for review
                </button>
              </div>
            ) : null}

            {phase === "submitting" ? (
              <div className="chat-add-input-row">
                <span className="add-item-gate-count">
                  <Loader2 size={14} className="add-item-spin" /> Saving…
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Discard your changes?"
        message="You've started this conversation but haven't submitted yet. If you leave now, your answers will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep chatting"
        tone="danger"
        autoFocusConfirm={false}
        onConfirm={() => {
          setConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
